import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

type TrendType = 'up' | 'down' | 'neutral';
type StatVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

export interface StatCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: {
    value: string | number;
    type: TrendType;
    label?: string;
  } | TrendType;
  variant?: StatVariant;
  subtitle?: string;
  description?: string;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
}

const variantStyles: Record<StatVariant, { bg: string; icon: string; border: string }> = {
  default: {
    bg: 'bg-gray-100 dark:bg-secondary-700',
    icon: 'text-gray-500 dark:text-gray-400',
    border: 'border-gray-200 dark:border-secondary-600',
  },
  success: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    icon: 'text-green-600 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800',
  },
  warning: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    icon: 'text-yellow-600 dark:text-yellow-400',
    border: 'border-yellow-200 dark:border-yellow-800',
  },
  danger: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    icon: 'text-red-600 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800',
  },
  info: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    icon: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
  },
};

const trendColors: Record<TrendType, string> = {
  up: 'text-green-600 dark:text-green-400',
  down: 'text-red-600 dark:text-red-400',
  neutral: 'text-gray-500 dark:text-gray-400',
};

const TrendIcon: React.FC<{ type: TrendType }> = ({ type }) => {
  switch (type) {
    case 'up':
      return <TrendingUp className="w-4 h-4" />;
    case 'down':
      return <TrendingDown className="w-4 h-4" />;
    default:
      return <Minus className="w-4 h-4" />;
  }
};

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  trend,
  variant = 'default',
  subtitle,
  description,
  loading = false,
  onClick,
  className = '',
}) => {
  const styles = variantStyles[variant];

  // Normalize trend to always be an object or undefined
  const normalizedTrend = trend
    ? typeof trend === 'string'
      ? { type: trend as TrendType, value: '' }
      : trend
    : undefined;

  return (
    <div
      onClick={onClick}
      className={`
        bg-white dark:bg-secondary-800 rounded-xl border ${styles.border}
        p-4 sm:p-5
        ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}
        ${className}
      `}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {title}
          </p>

          {loading ? (
            <div className="mt-2 h-8 w-24 bg-gray-200 dark:bg-secondary-700 rounded animate-pulse" />
          ) : (
            <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
              {value}
            </p>
          )}

          {(subtitle || description) && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {subtitle || description}
            </p>
          )}

          {normalizedTrend && !loading && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${trendColors[normalizedTrend.type]}`}>
              <TrendIcon type={normalizedTrend.type} />
              {normalizedTrend.value && <span className="font-medium">{normalizedTrend.value}</span>}
              {normalizedTrend.label && (
                <span className="text-gray-500 dark:text-gray-400 ml-1">
                  {normalizedTrend.label}
                </span>
              )}
            </div>
          )}
        </div>

        {icon && (
          <div className={`p-2.5 rounded-lg ${styles.bg}`}>
            <div className={styles.icon}>{icon}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// Grid wrapper for multiple StatCards
interface StatCardGridProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
}

export const StatCardGrid: React.FC<StatCardGridProps> = ({
  children,
  columns = 4,
}) => {
  const colClasses = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid gap-4 ${colClasses[columns]}`}>
      {children}
    </div>
  );
};

export default StatCard;
